'use client';

import { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import { getContainer } from '@framework/di/getContainer';
import CreditFlowResearchRepository
  from '@/creditflowresearch/infrastructure/repository/CreditFlowResearchRepository';
import useFetchCollection from '@/creditflowresearch/infrastructure/state/useFetchCollection';
import { ThemeType } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/themeTypes';
import {
  IMarketArticle,
  IMarketArticlesResponse,
  IMarketArticlesTileBasePayload,
} from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/IMarketArticle';
import {
  initialArticleTilesCollectionFiltersState,
} from '@/creditflowresearch/application/ArticlesAndStats/Articles/ArticlesTile/constants';
import { useArticlesPayload } from '@/creditflowresearch/application/ArticlesAndStats/Articles/ArticlesTile/useArticlesFilter';
import useAutoFetch from '@/creditflowresearch/application/hooks/useAutoFetch';

interface IArticleServerSideResponse extends IMarketArticlesResponse {
  payload: IMarketArticlesTileBasePayload,
  hasServerError?: boolean,
}

interface IUseArticleTitleProps {
  initialData: IArticleServerSideResponse,
  themeType: ThemeType,
  isSubscribeButtonVisible?: boolean,
}

const useArticlesTile = ({
  initialData: {
    payload,
    hasServerError,
    ...data
  },
  themeType,
  isSubscribeButtonVisible = true,
}: IUseArticleTitleProps) => {
  const container = getContainer();
  const requestService = container.get<CreditFlowResearchRepository>(CreditFlowResearchRepository);
  const [isFirstFetching, setIsFirstFetching] = useState<boolean>(!hasServerError);
  const articlesPayload = useArticlesPayload(payload);
  const [search, setSearch] = useState('');

  const fetchArticles = useCallback(
    (payload: IMarketArticlesTileBasePayload) => requestService.getArticlesList(payload),
    [requestService],
  );

  const articlesData = useFetchCollection<IMarketArticle, IMarketArticlesTileBasePayload>(
      fetchArticles,
      {
        enabled: !isFirstFetching,
        payload: articlesPayload,
        initialValues: { collection: data.collection, totalCount: data.totalCount, ...initialArticleTilesCollectionFiltersState },
      },
  );

  useEffect(() => {
    articlesData.reset();
  }, [data.collection]);

  useAutoFetch(
    () => setIsFirstFetching(false),
    [JSON.stringify(articlesPayload), articlesData.page, articlesData.debouncedSearchString],
  );

  useEffect(() => articlesData.reset, []);

  return {
    articlesData,
    themeType,
    search,
    setSearch,
    isSubscribeButtonVisible,
  };
};

type ArticlesTileContextType = ReturnType<typeof useArticlesTile>;

export const ArticlesTileContext = createContext<ArticlesTileContextType>(null);

export const useArticlesTileContext = () => {
  const context = useContext(ArticlesTileContext);
  if (!context) {
    throw new Error('useArticlesTileContext must be used within the ArticlesTileContext');
  }
  return context;
};

interface IArticlesTileContextProviderProps {
  children: ReactNode,
  initialData: IArticleServerSideResponse,
  themeType: ThemeType,
  isSubscribeButtonVisible?: boolean,
}

const ArticlesTileContextProvider = ({
  children,
  initialData,
  themeType,
  isSubscribeButtonVisible,
}: IArticlesTileContextProviderProps) => (
  <ArticlesTileContext.Provider value={ useArticlesTile({
    initialData,
    themeType,
    isSubscribeButtonVisible,
  }) }
  >
    { children }
  </ArticlesTileContext.Provider>
);

export default ArticlesTileContextProvider;
